/**
 * Signature-Section „360° Transformationen".
 *
 * Verhalten (neu · Multi-Active-Lazy):
 *  - Jede Kachel im Grid rendert DIREKT einen `BeforeAfter360Viewer` mit
 *    sichtbarem Gold-Handle und Vorher/Nachher-Labels. Kein Blur-Overlay,
 *    kein „Enthüllen"-Button, kein activeId-State.
 *  - Perf-Guardrail über `InViewportWrapper`:
 *      · Kachel > 1200 px aus dem Viewport → Viewer wird UNMOUNTED
 *        (Renderer/Textures dispose'd, WebGL-Kontext freigegeben).
 *      · Kachel im 200-px-Puffer um den Viewport → gemountet, aber rAF-Loop
 *        über `renderPaused` gesteuert (off-screen: paused, on-screen: aktiv).
 *      · Ergebnis: max 2–3 aktive WebGL-Kontexte gleichzeitig, egal wieviele
 *        Kacheln das CMS liefert.
 *  - Layout: Titel + Beschreibung als dezenter Overlay unten in der Kachel,
 *    damit der Slider die visuelle Dominanz behält.
 */
import { useCallback, useState } from "react";
import { useSiteContent } from "@/lib/siteContent";
import { mediaProxyUrlForced, type MediaRef } from "@/lib/media";
import BeforeAfter360Viewer from "@/components/BeforeAfter360Viewer";
import InViewportWrapper from "@/components/InViewportWrapper";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import { useMediaAiFlags } from "@/lib/aiFlags";

interface Transformation360Item {
    id: string;
    title: string;
    description?: string;
    thumbnail?: MediaRef;
    before?: MediaRef;
    after?: MediaRef;
}

interface Transformations360SectionData {
    eyebrow?: string;
    headline?: string;
    subheadline?: string;
    intro?: string;
    items?: Transformation360Item[];
}

function refToId(ref: MediaRef): string | undefined {
    if (!ref) return undefined;
    if (typeof ref === "string") return ref;
    return ref.media_id;
}

export default function Transformations360Section() {
    const { getObject } = useSiteContent();
    const data = getObject<Transformations360SectionData>("home.transformations360", {});
    const items = data.items || [];

    /** Map<tileId, inNearViewport?> — vom InViewportWrapper via
     *  onEnter/onLeave gepflegt. Steuert direkt `renderPaused` des Viewers. */
    const [inView, setInView] = useState<Record<string, boolean>>({});

    const markInView = useCallback((id: string, v: boolean) => {
        setInView((prev) => (prev[id] === v ? prev : { ...prev, [id]: v }));
    }, []);

    // Sammle alle Vorher/Nachher-Media-IDs → batch-load `is_ai_generated`.
    const aiFlags = useMediaAiFlags(
        items.flatMap((it) => [refToId(it.before), refToId(it.after)]),
    );

    if (items.length === 0) return null;

    return (
        <section
            className="relative py-24 md:py-32"
            style={{ background: "hsl(var(--hf-bg))" }}
            data-testid="tr360-section"
        >
            <div className="max-w-6xl mx-auto px-6">
                {(data.eyebrow || data.headline) && (
                    <div className="mb-12">
                        {data.eyebrow && (
                            <div
                                className="text-[11px] uppercase tracking-[0.24em] font-medium mb-3"
                                style={{ color: "hsl(var(--hf-gold))" }}
                            >
                                {data.eyebrow}
                            </div>
                        )}
                        {data.headline && (
                            <h2
                                className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight"
                                style={{ color: "hsl(var(--hf-ink))" }}
                            >
                                {data.headline}
                            </h2>
                        )}
                        {data.subheadline && (
                            <div
                                className="mt-3 text-lg font-light"
                                style={{ color: "hsl(var(--hf-ink) / 0.7)" }}
                            >
                                {data.subheadline}
                            </div>
                        )}
                        {data.intro && (
                            <p
                                className="mt-6 max-w-2xl text-base leading-relaxed"
                                style={{ color: "hsl(var(--hf-ink) / 0.75)" }}
                            >
                                {data.intro}
                            </p>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {items.map((item) => {
                        const thumbId = refToId(item.thumbnail) || refToId(item.after);
                        const beforeId = refToId(item.before);
                        const afterId = refToId(item.after);
                        const hasMedia = Boolean(beforeId && afterId);
                        const isNearView = Boolean(inView[item.id]);

                        return (
                            <div
                                key={item.id}
                                className="relative overflow-hidden rounded-lg"
                                style={{
                                    aspectRatio: "16/10",
                                    background: "hsl(var(--hf-ink) / 0.05)",
                                }}
                                data-testid={`tr360-tile-${item.id}`}
                                data-in-view={isNearView ? "true" : "false"}
                            >
                                {hasMedia ? (
                                    <ViewerErrorBoundary
                                        testId={`ba360-${item.id}-fallback`}
                                        fallbackTitle="360°-Vergleich derzeit nicht darstellbar"
                                    >
                                        <InViewportWrapper
                                            rootMargin="200px"
                                            unmountMargin="1200px"
                                            onEnter={() => markInView(item.id, true)}
                                            onLeave={() => markInView(item.id, false)}
                                            className="absolute inset-0"
                                            fallback={<TileSkeleton thumbId={thumbId} />}
                                        >
                                            <BeforeAfter360Viewer
                                                key={`viewer-${item.id}`}
                                                beforeUrl={mediaProxyUrlForced(beforeId!)}
                                                afterUrl={mediaProxyUrlForced(afterId!)}
                                                renderPaused={!isNearView}
                                                autoTour={true}
                                                testIdPrefix={`ba360-${item.id}`}
                                                mediaId={item.id}
                                                mediaTitle={item.title || `Transformation ${item.id.slice(0, 6)}`}
                                                beforeAi={!!aiFlags[beforeId!]}
                                                afterAi={!!aiFlags[afterId!]}
                                            />
                                        </InViewportWrapper>
                                    </ViewerErrorBoundary>
                                ) : (
                                    <TileSkeleton thumbId={thumbId} />
                                )}

                                {/* Titel-Overlay unten, halbtransparent damit
                                 *  der Slider dominiert. `pointer-events:none`
                                 *  damit Drag durchgereicht wird. */}
                                <div
                                    className="absolute left-0 right-0 bottom-0 px-5 pb-4 pt-8 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(to bottom, rgba(14,23,38,0) 0%, rgba(14,23,38,0.55) 45%, rgba(14,23,38,0.85) 100%)",
                                        color: "#faf8f3",
                                    }}
                                >
                                    <div className="text-base md:text-lg font-medium leading-snug drop-shadow">
                                        {item.title}
                                    </div>
                                    {item.description && (
                                        <div className="text-xs md:text-sm mt-1 opacity-85 line-clamp-2 drop-shadow">
                                            {item.description}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/** Skeleton — Thumb (falls vorhanden) + subtiler Shimmer. Wird gerendert,
 *  wenn Kachel > 1200 px off-viewport ist (Viewer unmounted). */
function TileSkeleton({ thumbId }: { thumbId?: string }) {
    return (
        <div
            className="absolute inset-0 overflow-hidden"
            style={{
                background:
                    "linear-gradient(90deg, hsl(var(--hf-paper)) 0%, hsl(var(--hf-bg)) 50%, hsl(var(--hf-paper)) 100%)",
                backgroundSize: "200% 100%",
                animation: "hf-shimmer 2.2s linear infinite",
            }}
            aria-hidden
        >
            {thumbId && (
                <img
                    src={mediaProxyUrlForced(thumbId)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                    loading="lazy"
                />
            )}
        </div>
    );
}
